/**
 * O rodapé "Total Geral" da tabela ANÁLISE MÍDIA PAGA, da Visão Geral.
 *
 * A tabela tem duas linhas — Meta Ads e Google Ads — e dez colunas, e só seis delas são
 * somáveis: sessões, pedidos, investimento, receita e as duas participações (`% Sessões
 * Total` e `% Marketing`, que são fatias do mesmo denominador e por isso fecham em 100%).
 * As outras quatro são **razões**: CPS é investimento por sessão, TX é pedidos por sessão,
 * TM é receita por pedido e ROAS é receita por investimento. Somar razão linha a linha não
 * dá razão nenhuma, e tirar a média de duas participações complementares dá sempre 50%.
 *
 * Era exatamente isso que a tela publicava: sem um `total` explícito, `StandardizedTable`
 * monta o rodapé sozinho, somando as colunas numéricas e tirando a média das percentuais.
 * A auditoria da Magnata (item 2 da fila P0, janela de 25 a 31/08/2026) leu CPS 1,37,
 * TM R$ 1.701,85, ROAS 6,55 e participação 50% — os três primeiros são as somas, o último é
 * a média de duas fatias que somam 100%.
 *
 * A regra que esta função aplica é a da auditoria: **todo total de razão se recalcula a
 * partir dos numeradores e denominadores agregados**, nunca como soma da coluna já dividida.
 */

/**
 * As dez colunas da tabela, na forma em que o card as monta.
 *
 * Todas opcionais porque quem chama é `StandardizedTable`, que entrega as linhas como
 * `TableRow` — um mapa de chave para valor de célula. Ler uma coluna ausente como zero é o
 * mesmo que a tabela faz ao desenhá-la ("N/D").
 */
export interface LinhaDeMidiaPaga {
  readonly sessoes?: number | null;
  readonly sessoesTotal?: number | null;
  readonly cps?: number | null;
  readonly pedidos?: number | null;
  readonly txConversao?: number | null;
  readonly tm?: number | null;
  readonly investimento?: number | null;
  readonly receitaTotal?: number | null;
  readonly roas?: number | null;
  readonly marketing?: number | null;
  // A linha que a tabela entrega carrega também `origem` e as duas variações; o rodapé não
  // as usa, e é esta assinatura que deixa uma `TableRow` entrar aqui sem conversão.
  readonly [coluna: string]: unknown;
}

export type RodapeDeMidiaPaga = {
  origem: string;
  sessoes: number;
  sessoesTotal: number;
  cps: number;
  pedidos: number;
  txConversao: number;
  tm: number;
  investimento: number;
  receitaTotal: number;
  roas: number;
  marketing: number;
};

/** As seis colunas aditivas — as únicas que o rodapé soma. */
type ColunaSomavel =
  | "sessoes"
  | "sessoesTotal"
  | "pedidos"
  | "investimento"
  | "receitaTotal"
  | "marketing";

const numero = (valor: unknown): number =>
  typeof valor === "number" && Number.isFinite(valor) ? valor : 0;

/**
 * Duas casas, que é o que todas as colunas desenham.
 *
 * Sem isso o rodapé publica o ruído do ponto flutuante: `% Marketing` é renderizado sem
 * `toFixed`, e 76,28 + 23,72 dá 100,00000000000001 em JavaScript.
 */
const duasCasas = (valor: number): number => Math.round(valor * 100) / 100;

/** `a / b`, com zero no lugar da divisão por zero — a tabela desenha "N/D" nesse caso. */
const razao = (numerador: number, denominador: number): number =>
  denominador === 0 ? 0 : numerador / denominador;

/**
 * O rodapé das linhas exibidas, ou `undefined` quando não há linha nenhuma.
 *
 * Entra em `StandardizedTable` pela prop `computeTotal`, e não por `total`, porque o rodapé
 * tem de acompanhar a busca e os filtros da própria tabela: quem chama de fora só conhece as
 * duas linhas do card, e um total das duas embaixo de uma tabela filtrada em uma delas seria
 * o padrão B da auditoria de cabeça para baixo. `computeTotal` recebe as linhas já filtradas.
 *
 * `undefined` faz a tabela não desenhar rodapé — que é o certo numa tabela vazia, e é também
 * o que ela já fazia antes desta função existir.
 */
export function rodapeDeMidiaPaga(
  linhas: readonly LinhaDeMidiaPaga[],
): RodapeDeMidiaPaga | undefined {
  if (linhas.length === 0) return undefined;

  const somar = (chave: ColunaSomavel): number =>
    linhas.reduce((acumulado, linha) => acumulado + numero(linha[chave]), 0);

  const sessoes = somar("sessoes");
  const pedidos = somar("pedidos");
  const investimento = somar("investimento");
  const receita = somar("receitaTotal");

  return {
    origem: "Total Geral",
    sessoes,
    // As duas participações são fatias do mesmo denominador: somam, não se promediam.
    sessoesTotal: duasCasas(somar("sessoesTotal")),
    cps: duasCasas(razao(investimento, sessoes)),
    pedidos,
    txConversao: duasCasas(razao(pedidos, sessoes) * 100),
    tm: duasCasas(razao(receita, pedidos)),
    investimento: duasCasas(investimento),
    receitaTotal: duasCasas(receita),
    roas: duasCasas(razao(receita, investimento)),
    marketing: duasCasas(somar("marketing")),
  };
}
